import { validatePuzzle, type PuzzleDefinition } from '../../domain/puzzle/PuzzleDefinition';
import { fixtures } from '../../domain/puzzle/fixtures';
import { difficulties, qualityTier } from '../../domain/puzzle/catalog';
import type { Settings } from '../../features/settings/settings';
import type { PersonalStore, Aggregate } from '../indexeddb/PersonalStore';
import { adaptiveRange, adaptiveStart } from '../../domain/puzzle/adaptive';

/** The rating range a practice setting asks for. Adaptive uses the hidden range worked out on launch. */
export function practiceRange(s: Settings): readonly [number, number] {
  if (s.difficulty === 'adaptive') return s.adaptiveRange ?? adaptiveRange(adaptiveStart);
  if (s.difficulty === 'custom') return [s.ratingMin, s.ratingMax];
  return difficulties[s.difficulty];
}

/** R5: missed puzzles that are due now; if none are due yet, the upcoming ones (review early). */
export function reviewQueue(aggregates: Aggregate[], now = Date.now()) {
  const scheduled = aggregates
    .filter((a) => a && typeof a.puzzleId === 'string' && Number.isFinite(a.reviewDueAt))
    .sort((a, b) => a.reviewDueAt! - b.reviewDueAt!);
  const due = scheduled.filter((a) => a.reviewDueAt! <= now);
  return {
    due: due.length,
    upcoming: scheduled.length - due.length,
    ids: (due.length ? due : scheduled).map((a) => a.puzzleId),
  };
}
export interface PuzzleSource {
  batch(
    settings: Settings,
    signal: AbortSignal,
    recent: string[],
    count?: number,
  ): Promise<PuzzleDefinition[]>;
  byId(id: string, signal: AbortSignal): Promise<PuzzleDefinition>;
}
/**
 * Per-try limits. After the site sits idle the library can take tens of seconds to wake
 * (49 s after about two idle hours on 2026-10-12), so the first try waits longest; a retry
 * usually finds it awake.
 */
export const ATTEMPT_TIMEOUTS_MS = [60000, 12000] as const;
/** How long a load the player is waiting on runs before the label says so. */
export const SLOW_LOAD_MS = 3000;
export const SLOW_LOAD_LABEL = 'Still loading the puzzle library…';
async function request(url: string, init: RequestInit): Promise<unknown> {
  init.signal?.throwIfAborted();
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    throw new Error('Your device is offline.');
  for (let attempt = 0; ; attempt++) {
    let terminal = false;
    try {
      const response = await fetch(url, {
        ...init,
        signal: AbortSignal.any([init.signal!, AbortSignal.timeout(ATTEMPT_TIMEOUTS_MS[attempt])]),
      });
      if (response.ok) return await response.json();
      terminal = response.status < 500;
      throw new Error(`Puzzle service ${response.status}`);
    } catch (error) {
      if (terminal || attempt === ATTEMPT_TIMEOUTS_MS.length - 1 || init.signal?.aborted)
        throw error;
      await new Promise((resolve) => setTimeout(resolve, 150 + Math.random() * 200));
      init.signal?.throwIfAborted();
    }
  }
}
export const apiSource: PuzzleSource = {
  async batch(s, signal, recent, count = 12) {
    const data = (await request('/api/v1/puzzles/batch', {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        count,
        mode: s.mode === 'theme' ? 'theme' : 'mixed',
        // Adaptive travels as a custom range, so the Worker needs no new difficulty.
        difficulty: s.difficulty === 'adaptive' ? 'custom' : s.difficulty,
        ...(s.difficulty === 'custom' || s.difficulty === 'adaptive'
          ? { ratingMin: practiceRange(s)[0], ratingMax: practiceRange(s)[1] }
          : {}),
        theme: s.mode === 'theme' ? s.theme : undefined,
        quality: s.quality,
        recentPuzzleIds: recent.slice(-40),
      }),
    })) as { puzzles: PuzzleDefinition[] };
    if (!Array.isArray(data.puzzles)) throw new Error('Invalid batch');
    return data.puzzles;
  },
  async byId(id, signal) {
    const data = (await request(`/api/v1/puzzles/${encodeURIComponent(id)}`, { signal })) as {
      puzzle: PuzzleDefinition;
    };
    return validatePuzzle(data.puzzle);
  },
};
export class PuzzleQueue {
  private queue: PuzzleDefinition[] = [];
  private seen = new Set<string>();
  private cycle = new Set<string>();
  private generation = 0;
  private initialization = 0;
  private abort = new AbortController();
  private inflight?: Promise<void>;
  private reviewIds: string[] = [];
  private reviewCursor = 0;
  private lastShown?: string;
  private slowTimer?: ReturnType<typeof setTimeout>;
  private disposed = false;
  label = 'Loading your first position…';
  slowAfterMs = SLOW_LOAD_MS;
  exhausted = false;
  constructor(
    private store: PersonalStore,
    private settings: Settings,
    private source: PuzzleSource = apiSource,
    private fixtureMode = false,
    private notify: () => void = () => {},
  ) {}
  get count() {
    return this.queue.length;
  }
  async initialize(settings = this.settings) {
    if (this.disposed) return;
    this.settings = settings;
    const request = ++this.initialization;
    const [seen, lastShown] = await Promise.all([this.store.seen(), this.store.lastShown()]);
    if (request !== this.initialization) return;
    this.seen = seen;
    this.lastShown = lastShown;
    // Adaptive can be retuned while storage is opening. Use the latest range.
    await this.configure(this.settings);
  }
  async configure(s: Settings) {
    if (this.disposed) return;
    this.initialization++;
    this.abort.abort();
    this.stopSlowWatch();
    this.abort = new AbortController();
    this.generation++;
    const generation = this.generation;
    this.settings = s;
    this.queue = [];
    this.inflight = undefined;
    this.exhausted = false;
    this.reviewCursor = 0;
    let reviewIds: string[] = [];
    if (s.mode === 'favorites')
      reviewIds = (await this.store.all<{ id: string }>('favorites'))
        .filter((p) => p && typeof p.id === 'string')
        .map((p) => p.id);
    else if (s.mode === 'review')
      reviewIds = reviewQueue(await this.store.all<Aggregate>('puzzleAggregates')).ids;
    if (generation !== this.generation) return;
    this.reviewIds = reviewIds;
    await this.fill(1);
  }
  take(): PuzzleDefinition | undefined {
    if (this.disposed) return;
    const puzzle = this.queue.shift();
    if (puzzle) {
      this.seen.delete(puzzle.id);
      this.seen.add(puzzle.id);
      this.lastShown = puzzle.id;
      this.cycle.add(puzzle.id);
      void this.store.markSeen(puzzle);
    }
    if (this.queue.length < 6) void this.fill(12, true);
    this.notify();
    return puzzle;
  }
  /** A background refill stays quiet; a load the player is waiting on says when it is slow. */
  async fill(target = 12, background = false) {
    if (this.disposed) return;
    if (!background) this.watchSlow();
    if (this.inflight) return this.inflight;
    const generation = this.generation,
      signal = this.abort.signal,
      settings = this.settings;
    const work = async () => {
      try {
        if (generation !== this.generation || signal.aborted) return;
        if (['favorites', 'review'].includes(settings.mode)) {
          const failed: string[] = [];
          const end = this.reviewIds.length;
          while (this.queue.length < target && this.reviewCursor < end) {
            if (generation !== this.generation || signal.aborted) return;
            const id = this.reviewIds[this.reviewCursor++];
            try {
              const p = await this.exact(id, signal);
              if (generation !== this.generation || signal.aborted) return;
              this.queue.push(p);
            } catch {
              if (generation !== this.generation || signal.aborted) return;
              failed.push(id);
            }
          }
          if (generation === this.generation) {
            // Keep failed positions pending so Retry can actually retrieve them.
            this.reviewIds = [...this.reviewIds.slice(this.reviewCursor), ...failed];
            this.reviewCursor = 0;
            this.exhausted = this.queue.length === 0 && this.reviewCursor >= this.reviewIds.length;
            this.label = this.queue.length
              ? settings.mode === 'review'
                ? 'Review · puzzles you missed'
                : 'Your saved positions'
              : this.reviewIds.length && this.reviewCursor < this.reviewIds.length
                ? 'Saved positions are unavailable. Retry when connected.'
                : settings.mode === 'review'
                  ? 'Review done. Nothing else is due.'
                  : this.reviewIds.length
                    ? 'Saved positions are unavailable. Retry when connected.'
                    : 'No saved positions here yet.';
          }
          return;
        }
        if (this.fixtureMode) {
          // Keep a deterministic loop for tests, but resume after the last shown
          // fixture across launches instead of restarting the same head.
          const last = fixtures.findIndex((p) => p.id === this.lastShown);
          const ordered = [...fixtures.slice(last + 1), ...fixtures.slice(0, last + 1)];
          let next = fixtures.filter(
            (p) => !this.cycle.has(p.id) && !this.queue.some((q) => q.id === p.id),
          );
          if (!next.length && !this.queue.length) {
            this.cycle.clear();
            next = ordered;
          }
          if (!this.cycle.size && !this.queue.length) next = ordered;
          this.queue.push(...next);
          this.label = 'Sample practice · 4 official examples';
          return;
        }
        for (let attempt = 0; attempt < 3 && this.queue.length < target; attempt++) {
          const puzzles = await this.source.batch(
            settings,
            signal,
            [...this.seen, ...this.queue.map((p) => p.id)].slice(-40),
            // A fast one-position startup draw can contain only older seen IDs
            // (the API accepts 40 recent exclusions). Widen later tries locally.
            Math.min(24, Math.max(target, attempt * 12)),
          );
          if (generation !== this.generation) return;
          for (const p of puzzles)
            try {
              const valid = validatePuzzle(p);
              if (!this.seen.has(valid.id) && !this.queue.some((q) => q.id === valid.id))
                this.queue.push(valid);
            } catch (e) {
              void this.store.put('badPuzzleRecords', p?.id ?? 'unknown', {
                id: p?.id,
                reason: String(e),
                at: Date.now(),
              });
            }
          if (this.queue.length)
            this.label = this.queue[0].datasetVersion.includes('1000')
              ? 'Practice library · 1,000-puzzle sample'
              : 'Your practice library';
        }
        if (generation === this.generation) {
          this.queue = this.queue.slice(0, 24);
          this.exhausted = this.queue.length === 0;
          if (this.exhausted)
            this.label = 'No new matches found. Widen your filters or practice again.';
          void this.store.cache([...this.queue]);
        }
      } catch (e) {
        if (signal.aborted || generation !== this.generation) return;
        const range = practiceRange(settings);
        const saved = await this.store.cached();
        const cached = (Array.isArray(saved) ? saved : []).flatMap((record) => {
          try {
            const p = validatePuzzle(record);
            return p.id !== this.lastShown &&
              !this.seen.has(p.id) &&
              p.rating >= range[0] &&
              p.rating <= range[1] &&
              (settings.quality === 'all' ||
                qualityTier(p.popularity, p.playCount, p.ratingDeviation) <= 2) &&
              (settings.mode !== 'theme' || p.themes.includes(settings.theme))
              ? [p]
              : [];
          } catch {
            return [];
          }
        });
        for (let i = cached.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cached[i], cached[j]] = [cached[j], cached[i]];
        }
        if (generation !== this.generation) return;
        for (const p of cached)
          try {
            if (!this.queue.some((q) => q.id === p.id)) this.queue.push(validatePuzzle(p));
          } catch {
            /* Ignore corrupt cache. */
          }
        const reason =
          (e as { name?: string })?.name === 'TimeoutError'
            ? 'It took too long to answer.'
            : e instanceof Error
              ? e.message
              : '';
        this.label = this.queue.length
          ? 'Offline · saved puzzle queue'
          : `Puzzle library unavailable. Retry or open sample practice. ${reason}`;
      } finally {
        if (generation === this.generation) {
          this.stopSlowWatch();
          this.inflight = undefined;
          this.notify();
        }
      }
    };
    this.inflight = Promise.resolve().then(work);
    await this.inflight;
  }
  private watchSlow() {
    if (this.slowTimer) return;
    const generation = this.generation;
    this.slowTimer = setTimeout(() => {
      if (generation !== this.generation || !this.inflight || this.queue.length) return;
      this.label = SLOW_LOAD_LABEL;
      this.notify();
    }, this.slowAfterMs);
  }
  private stopSlowWatch() {
    clearTimeout(this.slowTimer);
    this.slowTimer = undefined;
  }
  async exact(id: string, signal = this.abort.signal) {
    signal = AbortSignal.any([signal, this.abort.signal]);
    signal.throwIfAborted();
    if (this.fixtureMode) {
      const p = fixtures.find((p) => p.id === id);
      if (p) return p;
    }
    // R5: missed puzzles and favourites are kept on the device, so review works offline.
    const saved = await this.store.savedPuzzle(id);
    signal.throwIfAborted();
    if (saved)
      try {
        const valid = validatePuzzle(saved);
        if (valid.id === id) return valid;
      } catch {
        /* Fall back to the library. */
      }
    const puzzle = validatePuzzle(await this.source.byId(id, signal));
    signal.throwIfAborted();
    if (puzzle.id !== id) throw new Error('Puzzle service returned a different position.');
    return puzzle;
  }
  /**
   * R5: the hidden adaptive range moved. Keep queued puzzles that still fit, drop the
   * rest, and top the queue back up, without interrupting the current puzzle.
   */
  retune(s: Settings) {
    if (this.disposed) return;
    if (this.settings.mode !== s.mode || this.settings.difficulty !== 'adaptive') return;
    // An older refill must not put puzzles from the previous range back in the queue.
    this.abort.abort();
    this.stopSlowWatch();
    this.abort = new AbortController();
    this.generation++;
    this.inflight = undefined;
    this.settings = s;
    const [min, max] = practiceRange(s);
    this.queue = this.queue.filter((p) => p.rating >= min && p.rating <= max);
    if (this.queue.length < 6) void this.fill(12, true);
  }
  async practiceAgain() {
    if (this.disposed) return;
    this.seen.clear();
    this.cycle.clear();
    this.exhausted = false;
    await this.configure(this.settings);
  }
  async resetProgress() {
    this.seen.clear();
    this.cycle.clear();
    this.lastShown = undefined;
    await this.configure(this.settings);
  }
  dispose() {
    this.disposed = true;
    this.initialization++;
    this.stopSlowWatch();
    this.generation++;
    this.abort.abort();
    this.queue = [];
  }
}
